import { useTranslation } from "react-i18next";
import { FileSpreadsheet, FileText, Loader2, X } from "lucide-react";
import type { DocumentoAdjunto } from "@/lib/documentos/subir";

/**
 * Un documento subido al asistente (28-09-2026): en la caja de escribir (con «×»
 * para quitarlo antes de enviar, y «Subiendo…» mientras sube) y en el mensaje de
 * la persona, donde se queda para que se vea qué se le pasó.
 */
export function AssistantDocumentChip({
  doc,
  subiendo,
  onQuitar,
}: {
  doc: Pick<DocumentoAdjunto, "nombre" | "tipo"> & Partial<Pick<DocumentoAdjunto, "hojas" | "filas">>;
  subiendo?: boolean;
  onQuitar?: () => void;
}) {
  const { t } = useTranslation();
  const Icono = doc.tipo === "docx" ? FileText : FileSpreadsheet;
  const color = doc.tipo === "docx" ? "#2B579A" : "#1D7A46";
  return (
    <span
      data-testid="assistant-document-chip"
      className="inline-flex max-w-full items-center gap-2 rounded-xl border border-border/50 bg-white/90 px-2.5 py-1.5 text-left shadow-sm"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${color}1A`, color }}>
        {subiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icono className="h-4 w-4" />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[12.5px] font-semibold text-foreground">{doc.nombre}</span>
        <span className="block truncate text-[11px] text-muted-foreground">
          {subiendo
            ? t("ipmAssistant.documents.uploading", "Subiendo…")
            : doc.filas != null
            ? t("ipmAssistant.documents.summary", "{{rows}} filas", { rows: doc.filas })
            : doc.tipo.toUpperCase()}
        </span>
      </span>
      {onQuitar && !subiendo && (
        <button
          type="button"
          onClick={onQuitar}
          aria-label={t("ipmAssistant.documents.remove", "Quitar")}
          className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}
